/* ===========================================================================
 * THE THREE STATES AN OPTION LIST CAN BE IN, and why a picker has to know.
 *
 * `LeaveRequestForm` wrote the argument out in full and a test pins it: reading
 * `data` alone off a query makes three very different situations render as ONE
 * thing — a dropdown holding nothing but "Select…".
 *
 *   • the list is still loading
 *   • the request FAILED (a feature gate, the network, a 500)
 *   • the company genuinely has no departments / worksites / suppliers yet
 *
 * Only the third is the reader's problem and only the third has an action they
 * can take. The other two are the app failing to say so.
 *
 * That lesson was learned once and wired by hand on one screen, which is why
 * eleven other pickers still had it: the fix cost fifteen lines per dropdown
 * and nobody was going to spend a hundred and twenty of them inside a form.
 * It belongs in the control, so it costs one prop.
 *
 * A FAILED LIST DISABLES THE CONTROL AND SAYS WHY (rules §3.11 — a blocked
 * control names its obstacle). It does not silently offer a short list: a
 * department filter that lost half its options narrows what the reader thinks
 * exists, and nothing on screen admits it.
 * ======================================================================== */

export interface OptionsLoadState {
  /** The query is in flight. */
  isLoading?: boolean;
  /** The query FAILED. Not "returned nothing" — that is an empty list. */
  isError?: boolean;
  /** Shown in place of the placeholder while loading. */
  loadingLabel?: string;
  /** Shown in place of the placeholder when the load failed. */
  failedLabel?: string;
}

/**
 * What a picker should draw for a given load state: whether to block it, and
 * what to say instead of the placeholder. Returns `null` for the ordinary case
 * so a caller can keep its own placeholder untouched.
 */
export function resolveOptionsLoadState(
  state: OptionsLoadState | undefined,
  placeholder: string | undefined
): { blocked: boolean; failed: boolean; placeholder: string | undefined } {
  if (state?.isError === true) {
    return {
      blocked: true,
      failed: true,
      placeholder: state.failedLabel ?? 'Could not load — try again shortly',
    };
  }
  if (state?.isLoading === true) {
    return { blocked: true, failed: false, placeholder: state.loadingLabel ?? 'Loading…' };
  }
  return { blocked: false, failed: false, placeholder };
}
